import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { REALTIME_SESSION_INVALID_REASONS, RealtimeAckMessage, RealtimeScopeInvalidatedPayload, RealtimeServerToClientMessage, RealtimeSessionInvalidPayload } from '@common/api/realtime';
import { Subscription } from 'rxjs';
import { AppService } from '@ep-om/app.service';
import { AuthService } from '@ep-om/project/auth/auth.service';
import { ProjectService } from '@ep-om/project/state/project/project.service';
import { RealtimeV2BatchActivitySyncService } from './realtime-v2-batch-activity-sync.service';
import { RealtimeV2EntitySyncService } from './realtime-v2-entity-sync.service';
import { REALTIME_V2_SCOPE_KEYS, RealtimeV2RequestAck } from './realtime-v2.models';
import { RealtimeV2SocketService } from './realtime-v2-socket.service';

@Injectable({
  providedIn: 'root',
})
export class RealtimeV2MessageDispatcherService {
  private started = false;
  private subscription = new Subscription();

  constructor(
    private appService: AppService,
    private authService: AuthService,
    private batchActivitySync: RealtimeV2BatchActivitySyncService,
    private entitySync: RealtimeV2EntitySyncService,
    private projectService: ProjectService,
    private router: Router,
    private socket: RealtimeV2SocketService,
  ) {}

  start(): void {
    if (this.started) {
      return;
    }

    this.started = true;
    this.subscription.add(
      this.socket.messages$.subscribe((message) => this.handleMessage(message)),
    );
    this.socket.registerRequestHandler((request, ack) => this.handleRequest(request, ack));
  }

  private handleMessage(message: RealtimeServerToClientMessage): void {
    console.log('REALTIME-V2] message', message)
    try {
      switch (message.type) {
        case 'scope_sync_started':
          this.entitySync.markScopeSyncStarted(message.payload);
          break;
        case 'scope_ready':
          this.entitySync.markScopeReady(message.payload);
          break;
        case 'entity_updates':
          this.entitySync.applyEntityUpdates(message.payload);
          break;
        case 'scope_invalidated':
          void this.handleScopeInvalidated(message.payload);
          break;
        case 'entity_set_changed':
        case 'sys_message':
          break;
        case 'current_user_updated':
          this.authService.updateUser(message.payload);
          break;
        case 'main_session_changed':
          // Elezione main tab autoritativa dal backend v2: guida la pipeline di refresh token.
          this.appService.updateMaintab(message.payload.isMain);
          break;
        case 'session_invalid':
          void this.handleSessionInvalid(message.payload);
          break;
        case 'command_accepted':
        case 'command_queued':
        case 'command_queue_updated':
        case 'command_rejected':
        case 'command_completed':
          break;
        default:
          this.assertNever(message);
      }
    } catch (error) {
      console.error('[REALTIME-V2] message handling failed', message, error);
    }
  }

  private async handleScopeInvalidated(payload: RealtimeScopeInvalidatedPayload): Promise<void> {
    console.warn('[REALTIME-V2] scope invalidated', payload);
    const projectId = this.getProjectIdFromScope(payload.scopeKey);
    if (!projectId) {
      return;
    }

    this.projectService.clearProjectContent(projectId);

    if (payload.reason === 'access_revoked') {
      if (this.projectService.query.getActiveId() === projectId) {
        this.projectService.setActive(null);
        await this.router.navigate(['/project']);
      }
      return;
    }

    if (this.projectService.query.getActiveId() !== projectId) {
      return;
    }

    try {
      await this.socket.enterProject(projectId, []);
    } catch (error) {
      console.warn('[REALTIME-V2] project scope resync failed', {
        projectId,
        reason: payload.reason,
        error,
      });
    }
  }

  private async handleSessionInvalid(payload: RealtimeSessionInvalidPayload): Promise<void> {
    console.warn('[REALTIME-V2] session invalidated', payload);

    // Disconnessione forzata per flood di comandi: il backend ci ha esclusi. Facciamo un dump dello
    // stato (placeholder, da definire) e poi resettiamo completamente il client tornando al login.
    //if (payload.reason === REALTIME_SESSION_INVALID_REASONS.COMMAND_RATE_LIMIT) {
      this.dumpClientStateForDebug(payload);
      await this.authService.logout();
    //}
  }

  // PLACEHOLDER: in futuro qui produrremo un dump dello stato del client (store Akita, comandi
  // pendenti, scope attivi, ...) per diagnosticare cosa ha causato il flood, prima del reset.
  private dumpClientStateForDebug(payload: RealtimeSessionInvalidPayload): void {
    console.error('[REALTIME-V2] forced disconnect (command flood) — state dump placeholder', payload);
    // TODO: implementare il dump effettivo dello stato client.
  }

  private getProjectIdFromScope(scopeKey: string): string | null {
    if (!scopeKey?.startsWith(REALTIME_V2_SCOPE_KEYS.PROJECT_PREFIX)) {
      return null;
    }

    return scopeKey.substring(REALTIME_V2_SCOPE_KEYS.PROJECT_PREFIX.length);
  }

  private handleRequest(request: RealtimeAckMessage, ack: RealtimeV2RequestAck): void {
    console.log('[REALTIME-V2] request', request);
    try {
      switch (request.payload.type) {
        case 'entity_sync':
          this.entitySync.applyEntitySync(request.payload.payload);
          break;
        case 'batch_activity_sync':
          this.batchActivitySync.applyBatchActivitySync(request.payload.payload);
          break;
        case 'sys_message':
          break;
        default:
          this.assertNever(request.payload);
      }

      ack(true);
    } catch (error) {
      console.error('[REALTIME-V2] request handling failed', request, error);
      ack(false, error, this.buildRequestAckContext(request));
    }
  }

  /**
   * Contesto applicativo allegato al rifiuto di un `ws_req`: permette alla telemetria server-side
   * di sapere QUALE sync è fallito, non solo che è fallito.
   */
  private buildRequestAckContext(request: RealtimeAckMessage): Record<string, any> {
    const context: Record<string, any> = { ackId: request.id };

    if (request.payload.type === 'entity_sync') {
      const page = request.payload.payload?.data;
      context.entityName = page?.entityName;
      context.scopeKey = page?.scopeKey;
      context.entityCount = Array.isArray(page?.entities) ? page.entities.length : undefined;
    }

    if (request.payload.type === 'batch_activity_sync') {
      const page = request.payload.payload?.data;
      context.entityName = page?.entityName;
      context.scopeKey = page?.scopeKey;
      context.activityCount = Array.isArray(page?.activities) ? page.activities.length : undefined;
    }

    return context;
  }

  private assertNever(value: never): never {
    throw new Error(`Unsupported realtime v2 message: ${JSON.stringify(value)}`);
  }
}
